import {
    AfterViewChecked,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FeatureNames } from 'src/app/shared/models/feature-names.enum';
import { Assets } from 'src/app/shared/models/IAssets.model';
import { AssetsService } from 'src/app/shared/services/account/asset.services';
import { FeaturesStatusService } from 'src/app/shared/services/util/features-status.service';
import { NftConversionService } from 'src/app/shared/services/util/nft.conversion.service';
import { forkJoin } from 'rxjs';
import {
    FinalizedStakingApplication,
    NFTStakingApplication,
    StakedNFT,
} from 'src/app/shared/models/INFTStaking';
import { AccountService } from 'src/app/shared/services/account/account.services';
import { IFiatCurrency } from 'src/app/shared/models/IFiatCurrency';
import BigNumber from 'bignumber.js';
import { CollectionsService } from 'src/app/shared/services/account/collections.service';
import { LanguagesEnum } from 'src/app/shared/models/languages.enum';
import { FormatStringService } from 'src/app/shared/services/util/format-string.service';
import { SnackbarComponent } from 'src/app/shared/snackbar/snackbar.component';
import { TranslationConstants } from 'src/app/shared/services/util/translation.constants';
import { Clipboard } from '@angular/cdk/clipboard';
import { SnackBarTheme } from 'src/app/shared/models/snackbar-theme.enum';
import { environment } from 'src/environments/environment';
import { CalculateRemainingAuctionTimeService } from 'src/app/shared/services/util/calculate-remaining-auction-time.service';
import { ModalSendComponent } from 'src/app/tokenization/my-assets/modal-send/modal-send.component';
import { ModalResellComponent } from 'src/app/tokenization/my-assets/modal-resell/modal-resell.component';
import { ModalAuctionComponent } from 'src/app/tokenization/my-assets/modal-auction/modal-auction.component';
import { ModalSplitComponent } from 'src/app/tokenization/my-assets/modal-split/modal-split.component';
import { Network } from '../../models/network.model';

@Component({
    selector: 'app-card-list',
    templateUrl: './card-list.component.html',
    styleUrls: ['./card-list.component.scss'],
})
export class CardListComponent implements OnInit, OnChanges, AfterViewChecked {
    @Input() public assetList: Array<Assets> = [];
    @Input() public isHome: boolean = false;
    @Input() public autoScroll: boolean = false;
    @Output() public reloadData: EventEmitter<boolean> = new EventEmitter(
        false
    );
    public loading: boolean = false;

    public isResellAllowed: boolean = false;
    public isNFTStakingEnabled: boolean = false;
    public isSendNFTAllowed: boolean = false;
    public isAuctionNFTAllowed: boolean = false;
    public currentPage: string = 'MY_ASSETS';
    public stakedContainerId: string = '';
    public campaignDetails: StakedNFT;
    public rewardIconId: string = '';
    public isAuthenticated: boolean = false;
    public selectedLanguage: LanguagesEnum = undefined;
    public fiatCurrency: IFiatCurrency = new IFiatCurrency();
    public isImageLoaded: boolean = false;
    public userLoggedId: string = '';
    public distributeOnNFTs?: boolean = false;
    public networks: Network[] = [];

    constructor(
        private readonly accountService: AccountService,
        private readonly featuresStatusService: FeaturesStatusService,
        private readonly assetsService: AssetsService,
        private readonly collectionService: CollectionsService,
        private readonly router: Router,
        private readonly nftConversionService: NftConversionService,
        private readonly dialog: MatDialog,
        private readonly formatStringService: FormatStringService,
        private readonly clipboard: Clipboard,
        private readonly snackbarComponent: SnackbarComponent,
        private readonly translationConstants: TranslationConstants,
        private readonly calculateRemainingAuctionTimeService: CalculateRemainingAuctionTimeService
    ) {}

    public ngOnInit(): void {
        forkJoin([
            this.featuresStatusService.getFeatureStatus(
                FeatureNames.NFT_RESALE
            ),
            this.featuresStatusService.getFeatureStatus(FeatureNames.SEND_NFT),
            this.featuresStatusService.getFeatureStatus(
                FeatureNames.STAKING_NFT
            ),
            this.featuresStatusService.getFeatureStatus(
                FeatureNames.AUCTION_NFT
            ),
            this.featuresStatusService.getFeatureStatus(
                FeatureNames.DISTRIBUTE_ON_NFTS
            ),
            this.accountService.isAuthenticated(false, false),
        ]).subscribe(
            ([
                ResellAllowed,
                SendNFTAllowed,
                NFTStakingEnabled,
                auctionEnabled,
                distributeOnNFTs,
                isAuthenticated,
            ]) => {
                this.isResellAllowed = ResellAllowed;
                this.isSendNFTAllowed = SendNFTAllowed;
                this.isNFTStakingEnabled = NFTStakingEnabled;
                this.isAuctionNFTAllowed = auctionEnabled;
                this.distributeOnNFTs = distributeOnNFTs;
                this.isAuthenticated = isAuthenticated ? true : false;
            }
        );

        this.collectionService.getNetworks(false).subscribe((networks) => {
            this.networks = networks;
        });

        this.defineCurrentPage();
        this.fiatCurrency = this.accountService.getFiatCurrency();
        this.selectedLanguage = this.accountService.getUserLanguage();
    }

    /**
     * As a workaround, the isHome variable was added to identify whether the component
     * is being used on the home page so that the "theme" class can be added to change
     * the look to dark.
     *
     * For example, even in dark mode, the asset card on the my assets screen remains in light mode.
     */
    public ngAfterViewChecked(): void {
        if (this.isHome) {
            let cards: any = document.getElementsByClassName('card-list-item');
            cards = Array.from(cards);

            if (cards.length > 0) {
                for (const card of cards) {
                    card.classList.add('theme');
                }
            }
        }
    }

    private defineCurrentPage(): void {
        if (
            this.router.url.includes('tokenization/my-assets') ||
            this.router.url === '/home'
        ) {
            this.currentPage = 'MY_ASSETS';
        } else {
            this.currentPage = '';
        }
    }

    public getEllipsis(value: string): string {
        return this.formatStringService.getEllipsisInTheMiddle(value, 12);
    }

    public ngOnChanges(): void {
        this.scrollCardAfterLoad();

        for (let asset of this.assetList) {
            if (asset?.collection?.price && !asset?.nftId) {
                this.collectionService
                    .getSumUsedByCollection(asset?.collection.id)
                    .subscribe((maxToBuy) => {
                        asset.availableTokens = new BigNumber(
                            asset?.collection['price']
                        )
                            .minus(new BigNumber(maxToBuy))
                            .toNumber() as any;
                    });
            }

            if (asset?.auction?.deadline) {
                asset.auctionTimeExpiration =
                    this.calculateRemainingAuctionTimeService.calculateRemainingAuctionTime(
                        asset
                    );
            }
        }
    }

    public displaySpecificInfoAsset(asset: Assets): boolean {
        if (
            asset?.collection_id === environment.collectionId &&
            this.currentPage
        ) {
            return true;
        } else {
            return false;
        }
    }

    public goToLink(asset: Assets): void {
        window.open(asset?.external_link, '_blank');
    }

    public openBlockchain(asset: any): void {
        let network = this.networks.find(
            (network) => network.id === asset?.collection?.network_id
        );

        if (network.nft_scan_url) {
            window.open(
                `${network?.nft_scan_url}/${asset?.collection?.contract_address}/${asset?.blockchainId}`,
                '_blank'
            );
        } else {
            window.open(
                `${network?.scan_url}nft/${asset?.collection?.contract_address}/${asset?.blockchainId}`,
                '_blank'
            );
        }
    }

    public copyContractAddress(asset: Assets): void {
        const copied: boolean = this.clipboard.copy(
            asset?.collection?.contract_address
        );
        if (copied) {
            this.snackbarComponent.openSnackBar(
                this.translationConstants.translate('snackbar.copied'),
                SnackBarTheme.success,
                3000
            );
        }
    }

    public scrollCardAfterLoad(): void {
        if (!this.autoScroll) {
            return;
        }
        const cardHeight = document.querySelector('.card')?.clientHeight;
        const optionsToScroll: ScrollToOptions = {
            behavior: 'smooth',
            top: window.scrollY + (cardHeight ? cardHeight : 460),
        };
        window.scrollTo(optionsToScroll);
    }

    public getCorrectPrice(asset: Assets): number {
        return this.assetsService.getAssetCorrectPrice(
            asset,
            this.isResellAllowed
        );
    }

    public getAssetOriginalPrice(asset: Assets): number {
        return this.assetsService.getAssetOriginalPrice(asset, null);
    }

    public getSymbom(asset: Assets): string {
        if (asset.collection?.priceCurrency) {
            return asset.collection.priceCurrency;
        } else {
            return this.fiatCurrency.symbol;
        }
    }

    public getMainPhoto(asset: any): string {
        return this.assetsService.getAssetMainPhoto(asset);
    }

    public goToDetails(asset: Assets) {
        if (asset) {
            //In case there is a nftId, it means we are talking about a resale, hence, we must to search a specific NFT which is stored in a different table
            //In case not, we are looking to a general NFT will be bought for the very first time
            if (asset.nftId) {
                this.router.navigate([
                    `/tokenization/product-details/nft:${asset.nftId}`,
                ]);
            } else {
                this.router.navigate([
                    `/tokenization/product-details/asset:${asset.id}`,
                ]);
            }
        }
    }

    public goToCollectionProfile(collectionId: string): void {
        if (!collectionId) {
            return;
        }
        this.router.navigate([
            `/tokenization/collection-profile/${collectionId}`,
        ]);
    }

    public openExternalLink(asset: Assets): void {
        window.open(asset.external_link, '_blank');
    }

    public goToStakingNft(): void {
        this.router.navigate(['/tokenization/stake-nft/staked']);
    }

    public getVariation(asset: Assets): string {
        return new BigNumber(
            new BigNumber(this.getCorrectPrice(asset))
                .dividedBy(this.getAssetOriginalPrice(asset))
                .multipliedBy(100)
        )
            .minus(100)
            .toFixed(2);
    }

    async download(asset: Assets): Promise<void> {
        const imageUrl = this.getMainPhoto(asset);
        
        try {
            // Carrega a imagem do NFT
            const img = await this.loadImage(imageUrl);
            
            // Configurações do canvas
            const canvasWidth = 800;
            const padding = 40;
            const imageSize = canvasWidth - (padding * 2);
            const headerHeight = 60;
            const infoHeight = 280;
            const canvasHeight = headerHeight + imageSize + infoHeight + padding;
            
            // Cria o canvas
            const canvas = document.createElement('canvas');
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const ctx = canvas.getContext('2d');
            
            if (!ctx) return;

            // Background gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight);
            gradient.addColorStop(0, '#1a1a2e');
            gradient.addColorStop(1, '#16213e');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);

            // Header com nome da coleção
            ctx.fillStyle = '#e94560';
            ctx.fillRect(0, 0, canvasWidth, headerHeight);
            ctx.font = 'bold 24px Arial, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.fillText(asset?.collection?.name || 'NFT Certificate', canvasWidth / 2, 40);

            // Desenha a imagem do NFT com bordas arredondadas
            const imgX = padding;
            const imgY = headerHeight + 20;
            
            // Cria um clipping path para bordas arredondadas
            ctx.save();
            this.roundRect(ctx, imgX, imgY, imageSize, imageSize, 16);
            ctx.clip();
            
            // Desenha a imagem mantendo proporção
            const scale = Math.max(imageSize / img.width, imageSize / img.height);
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            const offsetX = imgX + (imageSize - scaledWidth) / 2;
            const offsetY = imgY + (imageSize - scaledHeight) / 2;
            ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
            ctx.restore();

            // Borda da imagem
            ctx.strokeStyle = '#e94560';
            ctx.lineWidth = 3;
            this.roundRect(ctx, imgX, imgY, imageSize, imageSize, 16);
            ctx.stroke();

            // Área de informações
            const infoY = imgY + imageSize + 30;
            ctx.textAlign = 'left';

            // Nome do NFT
            ctx.font = 'bold 28px Arial, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.fillText(asset?.name || 'NFT', padding, infoY);

            // ID do Blockchain
            if (asset?.blockchainId) {
                ctx.font = '16px Arial, sans-serif';
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText(`ID: ${asset.blockchainId}`, padding, infoY + 30);
            }

            // Linha separadora
            ctx.strokeStyle = '#e94560';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(padding, infoY + 50);
            ctx.lineTo(canvasWidth - padding, infoY + 50);
            ctx.stroke();

            // Preço
            const price = this.getCorrectPrice(asset);
            if (price) {
                ctx.font = 'bold 32px Arial, sans-serif';
                ctx.fillStyle = '#4ecca3';
                ctx.fillText(`${this.getSymbom(asset)} ${price.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`, padding, infoY + 90);
            }

            // Informações adicionais em duas colunas
            ctx.font = '14px Arial, sans-serif';
            ctx.fillStyle = '#8b8b8b';
            
            const col1X = padding;
            const col2X = canvasWidth / 2;
            let infoLineY = infoY + 130;

            // Coluna 1
            if (asset?.collection?.creator_institution) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Empresa:', col1X, infoLineY);
                ctx.fillStyle = '#ffffff';
                ctx.fillText(asset.collection.creator_institution, col1X + 70, infoLineY);
            }

            // Coluna 2
            if (asset?.quantity) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Quantidade:', col2X, infoLineY);
                ctx.fillStyle = '#ffffff';
                ctx.fillText(String(asset.quantity), col2X + 90, infoLineY);
            }

            infoLineY += 25;

            // Contract Address
            if (asset?.collection?.contract_address) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Contrato:', col1X, infoLineY);
                ctx.fillStyle = '#ffffff';
                ctx.fillText(this.getEllipsis(asset.collection.contract_address), col1X + 70, infoLineY);
            }

            // Network
            if (asset?.collection?.network_id) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Rede:', col2X, infoLineY);
                ctx.fillStyle = '#ffffff';
                ctx.fillText(asset.collection.network_id, col2X + 45, infoLineY);
            }

            infoLineY += 25;

            // Data
            if (asset?.collection?.when) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Data:', col1X, infoLineY);
                ctx.fillStyle = '#ffffff';
                const date = new Date(asset.collection.when);
                ctx.fillText(date.toLocaleDateString('pt-BR'), col1X + 45, infoLineY);
            }

            // Tipo do NFT
            if (asset?.nft_package_type) {
                ctx.fillStyle = '#8b8b8b';
                ctx.fillText('Tipo:', col2X, infoLineY);
                ctx.fillStyle = '#ffffff';
                ctx.fillText(asset.nft_package_type, col2X + 45, infoLineY);
            }

            // Footer
            ctx.font = '12px Arial, sans-serif';
            ctx.fillStyle = '#555555';
            ctx.textAlign = 'center';
            ctx.fillText(`Gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, canvasWidth / 2, canvasHeight - 15);

            // Faz o download
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = `${asset?.name || 'nft'}-${asset?.nftId || 'certificate'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Erro ao gerar imagem do NFT:', error);
            this.snackbarComponent.openSnackBar(
                this.translationConstants.translate('snackbar.downloadError') || 'Erro ao fazer download',
                SnackBarTheme.error,
                3000
            );
        }
    }

    private loadImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    }

    private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }

    public sendAsset(asset: Assets) {
        if (
            (asset?.resale?.onResale && this.isResellAllowed) ||
            asset?.on_staking
        ) {
            return;
        }
        const dialogRef = this.dialog.open(ModalSendComponent, {
            // 'custom-modal' class to be responsive
            panelClass: ['send-asset-modal', 'custom-modal'],
            data: {
                asset,
            },
        });

        // If you confirmed the shipment, we need to update the asset list.
        dialogRef.afterClosed().subscribe((action) => {
            if (action) {
                this.reloadData.emit(true);
            }
        });
    }

    public changeDistributionType(
        asset: Assets,
        is_distribuition_fiat: boolean
    ): void {
        this.loading = true;
        this.assetsService
            .changeDistributionType({
                id: asset.nftId,
                is_distribuition_fiat: is_distribuition_fiat,
            })
            .subscribe(
                (success) => {
                    if (success) {
                        this.snackbarComponent.openSnackBar(
                            this.translationConstants.translate(
                                'banking.escrow.success'
                            ),
                            SnackBarTheme.success,
                            4000
                        );
                    } else {
                        this.snackbarComponent.openSnackBar(
                            this.translationConstants.translate(
                                'banking.escrow.error'
                            ),
                            SnackBarTheme.error,
                            4000
                        );
                    }
                },
                (error) => {
                    this.snackbarComponent.openSnackBar(
                        this.translationConstants.translate(
                            'banking.escrow.error'
                        ),
                        SnackBarTheme.error,
                        4000
                    );
                }
            )
            .add(() => {
                asset.is_distribuition_fiat = !asset.is_distribuition_fiat;
                this.loading = false;
            });
    }

    public resellAsset(asset: Assets, isEditing: boolean): void {
        if (asset?.on_staking) {
            return;
        }

        const dialogRef = this.dialog.open(ModalResellComponent, {
            // 'custom-modal' class to be responsive
            panelClass: ['resell-asset-modal', 'custom-modal'],
            data: { asset, isEditing },
        });

        dialogRef.afterClosed().subscribe((action) => {
            if (action) {
                this.reloadData.emit(true);
            }
        });
    }

    public openStakedModal(containerIndex: number): void {
        this.stakedContainerId = `staked-container-${containerIndex}`;
    }

    public openRewardModal(containerIndex: number): void {
        this.rewardIconId = `reward-icon-${containerIndex}`;
    }

    public checkPeriod(stakedConfig: FinalizedStakingApplication): string {
        return this.nftConversionService.checkPeriod(stakedConfig);
    }

    public calculateRemainingPeriod(
        applicationConfig: NFTStakingApplication
    ): number {
        return this.nftConversionService.calculateRemainingPeriod(
            applicationConfig
        );
    }

    public progressPercentage(percentage: number): number {
        return this.nftConversionService.calculateProgressPercentage(
            percentage
        );
    }

    public setResaleTooltip(asset: Assets): string {
        let tranlatePath: string = '';
        if (asset?.on_staking) {
            tranlatePath = 'myAssets.resellAssetStaking';
        } else if (asset?.auction) {
            tranlatePath = 'myAssets.resellAssetAuction';
        } else if (asset?.resale?.onResale) {
            tranlatePath = 'myAssets.editResale';
        } else {
            tranlatePath = 'myAssets.resell';
        }

        return tranlatePath;
    }

    public setAutionTooltip(asset: Assets): string {
        let tranlatePath: string = '';
        if (asset?.on_staking) {
            tranlatePath = 'myAssets.auctionAssetForStaking';
        } else if (asset?.resale?.onResale) {
            tranlatePath = 'myAssets.resellAssetStaking';
        } else if (asset?.auction) {
            tranlatePath = 'myAssets.assetAlreadyAtAuction';
        } else {
            tranlatePath = 'myAssets.auction';
        }

        return tranlatePath;
    }

    public setSplitTooltip(asset: Assets): string {
        let tranlatePath: string = 'myAssets.split';

        return tranlatePath;
    }

    public setSendTooltip(asset: Assets): string {
        let tranlatePath: string = '';
        if (asset?.on_staking) {
            tranlatePath = 'myAssets.sendAssetStaking';
        } else if (asset?.resale?.onResale) {
            tranlatePath = 'myAssets.sendAssetResale';
        } else if (asset?.auction) {
            tranlatePath = 'myAssets.sendAssetAuction';
        } else {
            tranlatePath = 'myAssets.sendAsset';
        }

        return tranlatePath;
    }

    public auctionAsset(asset: Assets): void {
        if (asset?.on_staking || asset?.resale?.onResale) {
            return;
        }

        const dialogRef = this.dialog.open(ModalAuctionComponent, {
            // 'custom-modal' class to be responsive
            panelClass: ['resell-asset-modal', 'custom-modal'],
            data: { asset },
        });

        dialogRef.afterClosed().subscribe((action) => {
            if (action) {
                this.reloadData.emit(true);
            }
        });
    }

    public splitAsset(asset: Assets): void {
        if (asset?.on_staking || asset?.resale?.onResale) {
            return;
        }

        const dialogRef = this.dialog.open(ModalSplitComponent, {
            // 'custom-modal' class to be responsive
            panelClass: ['resell-asset-modal', 'custom-modal'],
            data: { asset },
        });

        dialogRef.afterClosed().subscribe((action) => {
            if (action) {
                this.reloadData.emit(true);
            }
        });
    }

    public getAmountOfBidsText(quantity: number): string {
        if (quantity === 0) {
            return this.translationConstants.translate(
                'productDetails.noBidMade'
            );
        } else if (quantity === 1) {
            return this.translationConstants.translate(
                'productDetails.bidMade'
            );
        } else {
            return this.translationConstants.translate(
                'productDetails.bidsMade'
            );
        }
    }
}
